// Installation over Bluetooth — many new boards at ONE location in one job
// (owner, 2026-10-18: "25 devices at one location: assign all, room and name
// per device, Wi-Fi once").
//
//   job      owner + location (fixed) + default room + Wi-Fi
//   per row  name (default: its current one) + room (default: the job's)
//   run      1. SERVER — assignDevice for every row (row + all 3 mappings)
//            2. BLUETOOTH — the Wi-Fi to every row that assigned, one at a time
//            3. ONLINE — listen on <uno>/client; the location's saved-network
//               list is written only then (registerWhenDeviceOnline)
//
// ⚠️ ONLY UNASSIGNED BOARDS. An assigned one is a live customer's device; moving
// it from a bench list by a mis-tick is the mistake this refuses. Device Admin
// (Unassign first) is where a device changes hands.
//
// ⚠️ THE BOARDS HAVE NO WI-FI, THE PHONE DOES. Everything server-side goes from
// the phone; nothing waits for a board to be online until step 3.
//
// Two audiences, one panel: today a Super Admin on the BLE bench (any owner);
// later an owner adding their own new boards (owner = themself). The logic here
// takes the owner as a parameter and does not care which.

import { openSession } from '../ble/bleSession';
import { stopScan } from '../ble/bleClient';
import { buildAllRelays } from '../ble/bleConfig';
import { runBleBatch, batchCommand } from '../ble/bleBatch';
import { assignDevice, createRoom, COMMON_ROOM_ID } from '../devices/deviceAdmin';
import { registerWhenDeviceOnline } from '../wifi/registerWifi';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';
import {
  isOneEndpoint, spokenName, normaliseName, clashMessage,
} from '../devices/alexaNames';

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/** A room the job will CREATE, kept as a string id until it exists. */
export const NEW_ROOM_PREFIX = 'new:';
export const newRoomId = (name) => `${NEW_ROOM_PREFIX}${String(name).trim()}`;
export const isNewRoom = (id) => String(id).startsWith(NEW_ROOM_PREFIX);
const newRoomName = (id) => String(id).slice(NEW_ROOM_PREFIX.length);

/** Room label for a row, from the location's rooms. */
export function roomLabel(roomId, rooms, locationName) {
  if (roomId === '' || roomId == null) return '';
  if (isNewRoom(roomId)) return newRoomName(roomId);
  if (Number(roomId) === COMMON_ROOM_ID) return `Common (${locationName || 'location'})`;
  return (rooms || []).find((r) => String(r.RoomID) === String(roomId))?.RoomName || `room ${roomId}`;
}

/**
 * Blink a board's relays so the installer can see WHICH one it is.
 * ALLON / ALLOFF `times` times over one authenticated link, then off.
 * -> { ok } | { ok:false, error }
 */
export async function identifyDevice(device, { times = 3, onMs = 700, offMs = 600 } = {}) {
  if (!device || !device.uniqueNumber) return { ok: false, error: 'No unique number' };
  await stopScan().catch(() => {});
  const opened = await openSession(device.deviceId, device.uniqueNumber, {});
  if (!opened.ok) return { ok: false, error: opened.error || opened.code || 'Could not connect' };
  const { session } = opened;
  try {
    for (let i = 0; i < times; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await session.send(buildAllRelays(true));
      // eslint-disable-next-line no-await-in-loop
      await sleep(onMs);
      // eslint-disable-next-line no-await-in-loop
      await session.send(buildAllRelays(false));
      // eslint-disable-next-line no-await-in-loop
      await sleep(offMs);
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e && e.message) || 'Write failed' };
  } finally {
    await session.close().catch(() => {});
  }
}

/**
 * Alexa: a curtain / lock / RGB / WWH board is spoken "<Room> <Device name>"
 * (Common: the location), so those names must not repeat — against the owner's
 * account (`index`, may be null = cannot check) AND within this job.
 * Switch panels are spoken per BUTTON and their device name is free.
 *
 * rows: [{ uno, deviceId (DeviceID), name, roomId }]
 * -> [{ uno, message }] — empty when every name is fine
 */
export function checkJobNames(rows, { index, rooms, locationName }) {
  const problems = [];
  const seen = new Map();
  (rows || []).forEach((r) => {
    if (!isOneEndpoint(r.deviceId)) return;
    const place = Number(r.roomId) === COMMON_ROOM_ID && !isNewRoom(r.roomId)
      ? locationName : roomLabel(r.roomId, rooms, locationName);
    const spoken = spokenName(place, r.name);
    const k = normaliseName(spoken);
    if (!k) return;
    if (seen.has(k)) {
      problems.push({ uno: r.uno, message: `Alexa would hear “${spoken}” for both ${seen.get(k)} and ${r.uno}.` });
      return;
    }
    seen.set(k, r.uno);
    const clash = index && (index.get(k) || []).find((e) => e.uno !== r.uno);
    if (clash) problems.push({ uno: r.uno, message: clashMessage(clash) });
  });
  return problems;
}

/**
 * Step 1 — the server. One board at a time (each is 4 small writes); a failure
 * is reported on its row and does not stop the rest. New rooms are created once
 * per job, the first time a row needs them.
 *
 * rows: [{ uno, device (server row), name, roomId }]
 * -> { [uno]: { ok, error?, roomId? } }
 */
export async function assignAll(rows, {
  ownerId, locationId, onProgress = null, shouldStop = () => false,
}) {
  const out = {};
  const created = new Map();   // new-room id -> real RoomID
  // eslint-disable-next-line no-restricted-syntax
  for (const r of rows) {
    if (shouldStop()) { out[r.uno] = { ok: false, error: 'Stopped' }; break; }
    try {
      let roomId = r.roomId;
      if (isNewRoom(roomId)) {
        if (!created.has(roomId)) {
          // eslint-disable-next-line no-await-in-loop
          created.set(roomId, await createRoom(ownerId, locationId, newRoomName(roomId)));
        }
        roomId = created.get(roomId);
      }
      // eslint-disable-next-line no-await-in-loop
      await assignDevice({
        device: r.device, uniqueNumber: r.uno, userId: ownerId, locationId, roomId, deviceName: r.name,
      });
      out[r.uno] = { ok: true, roomId };
    } catch (e) {
      out[r.uno] = { ok: false, error: (e && e.message) || 'Could not assign' };
    }
    if (onProgress) onProgress({ ...out });
  }
  return { results: out, createdRooms: created };
}

/**
 * Step 2 — the Wi-Fi, over Bluetooth, to every board in `devices` (scan rows),
 * one at a time (bleBatch). Each one that took it is watched for its first MQTT
 * message, and only then is the network saved on the location.
 * -> { [uno]: { ok, reply?, error? } }
 */
export async function sendWifiAll(devices, {
  ssid, password, locationId, onProgress = null, shouldStop = () => false,
}) {
  const results = await runBleBatch(devices, batchCommand('addwifi'), { ssid, pass: password }, {
    shouldStop,
    onProgress: onProgress ? (list) => onProgress(Object.fromEntries(list.map((x) => [x.uniqueNumber, x]))) : null,
  });
  const out = Object.fromEntries(results.map((x) => [x.uniqueNumber, x]));
  results.filter((x) => x.ok).forEach((x) => {
    registerWhenDeviceOnline(x.uniqueNumber, locationId, ssid, password);
  });
  return out;
}

/**
 * Step 3 — which boards have spoken on MQTT since `since`. Status relayed from
 * a Bluetooth link ends in `#BLE` and is NOT proof of Wi-Fi, so it is ignored.
 * -> stop()
 */
export function watchOnline(unos, onHeard) {
  const topics = new Map((unos || []).map((u) => [`${u}/client`, String(u)]));
  topics.forEach((_, t) => subscribeTopic(t));
  const off = addMessageListener((topic, payload) => {
    const uno = topics.get(topic);
    if (!uno) return;
    if (String(payload ?? '').trim().endsWith('#BLE')) return;
    onHeard(uno);
  });
  return () => {
    off();
    topics.forEach((_, t) => unsubscribeTopic(t));
  };
}
